// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Table component for displaying sold assets snapshot.
 *
 * Shows assets that have been sold as of a specific date.
 * Uses react-window for virtualized rendering to handle large datasets efficiently.
 */
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { EmptyState } from '@/components/empty-state';
import { Link } from '@/components/link';
import { TruncatedText } from '@/components/truncated-text';
import {
  formatAmountWithCurrency,
  formatCurrencyValue,
} from '@/currency/utilities/format';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { SoldAssetSnapshotItem } from '@/report/api/types';
import { SnapshotReportLoader } from '@/report/components/snapshot-report-loader';
import { LeftInventoryFor } from '@/report/types';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import { Box, Chip, Paper, Tooltip, Typography } from '@mui/material';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

// Height of each row in pixels for virtualization
const ROW_HEIGHT = 57;

// Number of rows to pre-render outside the visible area
const OVERSCAN_COUNT = 10;

// Maximum height for the virtualized table body
const MAX_TABLE_HEIGHT = 800;

// Minimum width for horizontal scrolling on narrow screens
const MIN_TABLE_WIDTH = 1600;

// Grid template for consistent column widths between header and rows
const GRID_TEMPLATE_COLUMNS =
  'minmax(120px, 1.2fr) minmax(100px, 0.8fr) minmax(100px, 0.8fr) minmax(100px, 0.8fr) minmax(100px, 0.8fr) minmax(100px, 0.8fr) minmax(120px, 0.9fr) minmax(110px, 0.8fr) minmax(130px, 1fr) minmax(100px, 0.8fr) minmax(90px, 0.7fr) minmax(100px, 0.8fr) minmax(90px, 0.7fr)';

interface Props {
  assets: SoldAssetSnapshotItem[];
  isLoading: boolean;
  isAnimating?: boolean;
  onAnimationComplete?: () => void;
  organizationName: string;
}

// Helper function to format percentage values
const formatPercentage = (value: number) => {
  return `${(value * 100).toFixed(1)}%`;
};

// Helper function to render the left inventory for pill
const renderLeftInventoryForPill = (
  leftInventoryFor: LeftInventoryFor | null
) => {
  if (!leftInventoryFor) return <Typography variant="body2">—</Typography>;

  const color = leftInventoryFor === 'FIXED_ASSETS' ? 'primary' : 'success';
  const label = leftInventoryFor === 'FIXED_ASSETS' ? 'Fixed assets' : 'Sale';

  return <Chip label={label} color={color} size="small" />;
};

// Data passed to the virtualized row renderer, combining assets with the organization name
interface RowData {
  assets: SoldAssetSnapshotItem[];
  organizationName: string;
}

// Virtualized row renderer for the sold assets table
function VirtualizedRow(props: ListChildComponentProps<RowData>) {
  const asset = props.data.assets[props.index];

  return (
    <Box
      style={props.style}
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: 2,
      }}
    >
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        <Link
          to="/assets/$assetKey"
          color="primary.main"
          variant="subtitle2"
          params={{ assetKey: asset.key }}
        >
          {asset.serial}
        </Link>
        <TruncatedText
          text={`${asset.item.sku} (${asset.item.name})`}
          variant="caption"
          color="text.secondary"
        />
      </Box>
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        {asset.inTransit === true ? (
          <Typography variant="body2" noWrap>
            In transit
          </Typography>
        ) : asset.location ? (
          <>
            <TruncatedText
              text={getEffectiveLocationName(asset.location)}
              variant="body2"
            />
            <TruncatedText
              text={
                asset.location.customer?.name ?? props.data.organizationName
              }
              variant="caption"
              color="text.secondary"
            />
          </>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        {renderLeftInventoryForPill(asset.leftInventoryFor)}
      </Box>
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        {asset.inventoryDate ? (
          <>
            <Typography variant="body2" noWrap>
              {asset.inventoryDate.toLocaleString({
                dateStyle: 'medium',
              })}
            </Typography>
            {asset.daysInInventory !== null && (
              <ConditionalTooltip
                arrow
                tooltipText={`Time in inventory before ${asset.leftInventoryFor === 'FIXED_ASSETS' ? 'moving to fixed assets' : 'sale'}`}
              >
                <Typography variant="caption" color="text.secondary" noWrap>
                  {pluralize(asset.daysInInventory, {
                    singular: 'day',
                    plural: 'days',
                  })}
                </Typography>
              </ConditionalTooltip>
            )}
          </>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        {asset.accounting.inServiceDate ? (
          <>
            <Typography variant="body2" noWrap>
              {asset.accounting.inServiceDate.toLocaleString({
                dateStyle: 'medium',
              })}
            </Typography>
            {asset.daysInServiceOnBooks !== null && (
              <ConditionalTooltip
                arrow
                tooltipText={
                  asset.daysOfUsefulLife !== null
                    ? `${formatPercentage(
                        asset.daysInServiceOnBooks / asset.daysOfUsefulLife
                      )} of useful life (${formatNumber(
                        asset.daysOfUsefulLife
                      )} days)`
                    : undefined
                }
              >
                <Typography variant="caption" color="text.secondary" noWrap>
                  {pluralize(asset.daysInServiceOnBooks, {
                    singular: 'day on books',
                    plural: 'days on books',
                  })}
                </Typography>
              </ConditionalTooltip>
            )}
          </>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        {asset.saleDate ? (
          <>
            <Typography variant="body2" noWrap>
              {asset.saleDate.toLocaleString({
                dateStyle: 'medium',
              })}
            </Typography>
            {asset.daysInServiceOffBooks !== null && (
              <ConditionalTooltip
                arrow
                tooltipText={
                  asset.daysOfUsefulLife !== null
                    ? `${formatPercentage(
                        asset.daysInServiceOffBooks / asset.daysOfUsefulLife
                      )} of useful life (${formatNumber(
                        asset.daysOfUsefulLife
                      )} days)`
                    : undefined
                }
              >
                <Typography variant="caption" color="text.secondary" noWrap>
                  {pluralize(asset.daysInServiceOffBooks, {
                    singular: 'day off books',
                    plural: 'days off books',
                  })}
                </Typography>
              </ConditionalTooltip>
            )}
          </>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        {asset.endOfUsefulLife ? (
          <>
            <Typography variant="body2" noWrap>
              {asset.endOfUsefulLife.toLocaleString({
                dateStyle: 'medium',
              })}
            </Typography>
            <ConditionalTooltip
              arrow
              tooltipText={
                asset.daysOfUsefulLife !== null
                  ? `${pluralize(asset.daysOfUsefulLife, {
                      singular: 'day',
                      plural: 'days',
                    })}`
                  : undefined
              }
            >
              {asset.accounting.usefulLife !== null && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {pluralize(asset.accounting.usefulLife, {
                    singular: 'month',
                    plural: 'months',
                  })}
                </Typography>
              )}
            </ConditionalTooltip>
          </>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        {asset.depreciableAmount !== null ? (
          <Tooltip
            arrow
            title={
              <>
                Capitalized costs:{' '}
                {formatCurrencyValue(asset.accounting.capitalizedCosts)}
                <br />
                Salvage value:{' '}
                {formatCurrencyValue(asset.accounting.salvageValue)}
              </>
            }
          >
            <Typography variant="body2" noWrap>
              {formatAmountWithCurrency(
                asset.depreciableAmount,
                asset.accounting.costCurrencyCode,
                { sparse: true }
              )}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        {asset.accumulatedDepreciation !== null &&
        asset.depreciableAmount !== null ? (
          <>
            <Typography variant="body2" noWrap>
              {formatAmountWithCurrency(
                asset.accumulatedDepreciation,
                asset.accounting.costCurrencyCode,
                { sparse: true }
              )}
            </Typography>
            <ConditionalTooltip
              arrow
              tooltipText={`${formatAmountWithCurrency(asset.accumulatedDepreciation, asset.accounting.costCurrencyCode)} depreciated of ${formatAmountWithCurrency(asset.depreciableAmount, asset.accounting.costCurrencyCode)} depreciable`}
            >
              <Typography variant="caption" color="text.secondary" noWrap>
                {formatPercentage(
                  parseFloat(asset.accumulatedDepreciation) /
                    parseFloat(asset.depreciableAmount)
                )}{' '}
                depreciated
              </Typography>
            </ConditionalTooltip>
          </>
        ) : (
          <Typography variant="body2">n/a</Typography>
        )}
      </Box>
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        {asset.bookValueAtSale !== null &&
        asset.accounting.capitalizedCosts !== null ? (
          <>
            <Typography variant="body2" noWrap>
              {formatAmountWithCurrency(
                asset.bookValueAtSale,
                asset.accounting.costCurrencyCode,
                { sparse: true }
              )}
            </Typography>
            <ConditionalTooltip
              arrow
              tooltipText={
                `${formatAmountWithCurrency(asset.bookValueAtSale, asset.accounting.costCurrencyCode)} remaining of ${formatCurrencyValue(asset.accounting.capitalizedCosts)} capitalized costs` +
                `${asset.accounting.salvageValue ? ` (including ${formatCurrencyValue(asset.accounting.salvageValue)} salvage value)` : ''}`
              }
            >
              <Typography variant="caption" color="text.secondary" noWrap>
                {formatPercentage(
                  parseFloat(asset.bookValueAtSale) /
                    asset.accounting.capitalizedCosts.amount.toNumber()
                )}{' '}
                remaining
              </Typography>
            </ConditionalTooltip>
          </>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        {asset.saleAmount !== null ? (
          <>
            <Typography variant="body2" noWrap>
              {formatAmountWithCurrency(
                asset.saleAmount,
                asset.accounting.costCurrencyCode,
                { sparse: true }
              )}
            </Typography>
            {asset.saleCustomer && (
              <TruncatedText
                text={asset.saleCustomer.name}
                variant="caption"
                color="text.secondary"
              />
            )}
          </>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        {asset.saleGainLoss !== null && asset.bookValueAtSale !== null ? (
          <>
            <Typography
              variant="body2"
              noWrap
              color={
                parseFloat(asset.saleGainLoss) >= 0
                  ? 'success.main'
                  : 'error.main'
              }
            >
              {parseFloat(asset.saleGainLoss) >= 0 ? '' : '('}
              {formatAmountWithCurrency(
                parseCurrencyAmount(asset.saleGainLoss)?.abs().toString(),
                asset.accounting.costCurrencyCode,
                { sparse: true }
              )}
              {parseFloat(asset.saleGainLoss) < 0 ? ')' : ''}
            </Typography>
            <ConditionalTooltip
              arrow
              tooltipText={
                asset.saleAmount !== null
                  ? `${formatAmountWithCurrency(asset.saleAmount, asset.accounting.costCurrencyCode)} sale amount less ${formatAmountWithCurrency(asset.bookValueAtSale, asset.accounting.costCurrencyCode)} book value at sale = ${formatAmountWithCurrency(asset.saleGainLoss, asset.accounting.costCurrencyCode)} ${parseFloat(asset.saleGainLoss) >= 0 ? 'gain' : 'loss'}`
                  : undefined
              }
            >
              <Typography variant="caption" color="text.secondary" noWrap>
                {formatPercentage(
                  parseFloat(asset.saleGainLoss) /
                    parseFloat(asset.bookValueAtSale)
                )}{' '}
                {parseFloat(asset.saleGainLoss) >= 0 ? 'gain' : 'loss'}
              </Typography>
            </ConditionalTooltip>
          </>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
      <Box sx={{ py: 1, minWidth: 0, overflow: 'hidden' }}>
        {asset.scrapDate ? (
          <>
            <Typography variant="body2" noWrap>
              {asset.scrapDate.toLocaleString({
                dateStyle: 'medium',
              })}
            </Typography>
            {asset.scrapReason !== null && (
              <TruncatedText
                text={asset.scrapReason}
                variant="caption"
                color="text.secondary"
              />
            )}
          </>
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
    </Box>
  );
}

export function SoldAssetsSnapshotTable(props: Props) {
  // Calculate the height for the virtualized list
  const listHeight = Math.min(
    props.assets.length * ROW_HEIGHT,
    MAX_TABLE_HEIGHT
  );

  if (props.isLoading || props.isAnimating) {
    return (
      <SnapshotReportLoader
        reportType="sold"
        isDataReady={!props.isLoading}
        onComplete={props.onAnimationComplete}
      />
    );
  }

  if (props.assets.length === 0) {
    return (
      <EmptyState
        title="No sold assets found"
        message="There were no assets on this date that were sold before being scrapped"
      />
    );
  }

  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
      {/* Scrollable inner container with minimum width */}
      <Box sx={{ minWidth: MIN_TABLE_WIDTH }}>
        {/* Header row using CSS grid to match virtualized rows */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.100',
            px: 2,
            py: 1,
          }}
        >
          <Typography variant="overline">Serial</Typography>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            Location
          </Typography>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            Left inv. for
          </Typography>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            Inventory date
          </Typography>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            In-service date
          </Typography>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            Sale date
          </Typography>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            End of useful life
          </Typography>
          <Tooltip arrow title="Cost basis minus salvage value">
            <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
              Depreciable amt
            </Typography>
          </Tooltip>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            Accum. depr. at sale
          </Typography>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            Book value
          </Typography>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            Sale amount
          </Typography>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            Gain (loss)
          </Typography>
          <Typography variant="overline" sx={{ lineHeight: '1.5' }}>
            Disposal date
          </Typography>
        </Box>

        {/* Virtualized table body */}
        <FixedSizeList
          height={listHeight}
          width="100%"
          itemSize={ROW_HEIGHT}
          itemCount={props.assets.length}
          overscanCount={OVERSCAN_COUNT}
          itemData={{
            assets: props.assets,
            organizationName: props.organizationName,
          }}
        >
          {VirtualizedRow}
        </FixedSizeList>
      </Box>
    </Paper>
  );
}
